import { formatDate } from '../../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../../shared/DateTimeProvider';
import React, { useState, useEffect, useMemo } from 'react';
import { tenantApi, type ExchangeSenderRegistration } from '../../services/tenantApi';
import type { NotificationChannel } from '../../services/adminApi';
import { NotificationDeliveryQueue } from './NotificationDeliveryQueue';

const CHANNEL_TYPES = [
  { value: 'Teams', label: 'Teams (Incoming Webhook)', description: 'Classic connector webhook - sends MessageCard format' },
  { value: 'TeamsWorkflow', label: 'Teams (Workflow/Power Automate)', description: 'Power Automate webhook - sends Adaptive Card format' },
  { value: 'Email', label: 'Email Distribution List', description: 'Send to one or more email addresses' },
  { value: 'SMS', label: 'SMS', description: 'Send SMS to one or more phone numbers' },
  { value: 'Webhook', label: 'Generic Webhook', description: 'HTTP POST to any URL with custom headers' },
];

export const NotificationChannelsManagement: React.FC = () => {
  useDateTimePreferences();
  const [activeTab, setActiveTab] = useState<'channels' | 'deliveries'>('channels');
  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [eligibleSenders, setEligibleSenders] = useState<ExchangeSenderRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<NotificationChannel | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<NotificationChannel | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [messageDialog, setMessageDialog] = useState<{ title: string; message: string } | null>(null);

  useEffect(() => {
    loadChannels();
  }, []);

  const loadChannels = async () => {
    setLoading(true);
    try {
      const data = await tenantApi.getNotificationChannels();
      setChannels(data);
      setEligibleSenders(await tenantApi.getEligibleExchangeSenders().catch(() => []));
    } catch (err) {
      console.error('Failed to load notification channels:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data: { name: string; channelType: string; configJson: string; emailSenderIdentityId?: string | null }) => {
    await tenantApi.createNotificationChannel(data);
    setShowCreateModal(false);
    await loadChannels();
  };

  const handleUpdate = async (channelId: string, data: { name?: string; channelType?: string; configJson?: string; emailSenderIdentityId?: string | null }) => {
    await tenantApi.updateNotificationChannel(channelId, data);
    setShowEditModal(null);
    await loadChannels();
  };

  const handleDelete = (channel: NotificationChannel) => {
    setDeleteError(null);
    setDeleteTarget(channel);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleteSubmitting) return;
    setDeleteSubmitting(true);
    setDeleteError(null);
    try {
      await tenantApi.deleteNotificationChannel(deleteTarget.channelId);
      setDeleteTarget(null);
      await loadChannels();
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete channel');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleToggle = async (channelId: string) => {
    try {
      await tenantApi.toggleNotificationChannel(channelId);
      await loadChannels();
    } catch (err: any) {
      setMessageDialog({ title: 'Channel update failed', message: err.message || 'Failed to toggle channel' });
    }
  };

  const handleTest = async (channelId: string) => {
    try {
      const result = await tenantApi.testNotificationChannel(channelId);
      setMessageDialog({ title: 'Test notification', message: result.message || 'Test notification sent' });
    } catch (err: any) {
      setMessageDialog({ title: 'Test notification failed', message: err.message || 'Failed to send test' });
    }
  };

  const getChannelTypeLabel = (type: string): string => {
    const ct = CHANNEL_TYPES.find(t => t.value === type);
    return ct?.label || type;
  };

  const getSenderSummary = (channel: NotificationChannel): string | null => {
    if (channel.channelType !== 'Email' || !channel.emailSenderIdentityId) return null;
    const sender = eligibleSenders.find(item => item.senderIdentityId === channel.emailSenderIdentityId);
    return sender ? `${sender.mailboxName} · ${sender.label} <${sender.fromAddress}>` : 'Saved sender unavailable';
  };

  return (
    <div className="management-section notification-channels-view">
      <div className="notification-channel-tabs" role="tablist" aria-label="Notification channel views">
        <button type="button" role="tab" aria-selected={activeTab === 'channels'} onClick={() => setActiveTab('channels')}>Channels</button>
        <button type="button" role="tab" aria-selected={activeTab === 'deliveries'} onClick={() => setActiveTab('deliveries')}>Failed deliveries</button>
      </div>
      {activeTab === 'deliveries' ? <NotificationDeliveryQueue /> : <>
      <div className="section-header">
        <h2>Notification Channels ({channels.length})</h2>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          + Add Channel
        </button>
      </div>

      <p className="help-text" style={{ marginBottom: '1rem' }}>
        Configure reusable notification destinations. Channels can be used as targets in routing rules to notify Teams, email, SMS, or external webhooks.
      </p>

      {loading ? (
        <div className="loading">Loading channels...</div>
      ) : (
        <table className="data-table channels-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th className="status-column">Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {channels.map((channel) => (
              <tr key={channel.channelId}>
                <td><strong>{channel.name}</strong></td>
                <td>
                  {getChannelTypeLabel(channel.channelType)}
                  {getSenderSummary(channel) && <div className="help-text">Graph · {getSenderSummary(channel)}</div>}
                </td>
                <td className="status-column">
                  <span className={`status-badge ${channel.isEnabled ? 'enabled' : 'disabled'}`}>
                    {channel.isEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
                <td>{formatDate(channel.createdAt)}</td>
                <td>
                  <div className="channel-actions-row">
                  <button className="btn-icon channel-action-btn" title="Test" onClick={() => handleTest(channel.channelId)}>
                    &#x1F4E8;
                  </button>
                  <button className="btn-icon channel-action-btn" title="Edit" onClick={() => setShowEditModal(channel)}>
                    &#x270F;&#xFE0F;
                  </button>
                  <button className="btn-icon channel-action-btn" title={channel.isEnabled ? 'Disable' : 'Enable'} onClick={() => handleToggle(channel.channelId)}>
                    {channel.isEnabled ? '\u23F8' : '\u25B6'}
                  </button>
                  <button className="btn-icon channel-action-btn" title="Delete" aria-label={`Delete ${channel.name}`} onClick={() => handleDelete(channel)}>
                    &#x1F5D1;&#xFE0F;
                  </button>
                  </div>
                </td>
              </tr>
            ))}
            {channels.length === 0 && (
              <tr>
                <td colSpan={5} className="empty-row">No notification channels configured</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {showCreateModal && (
        <ChannelModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreate}
        />
      )}

      {showEditModal && (
        <ChannelModal
          channel={showEditModal}
          onClose={() => setShowEditModal(null)}
          onSave={(data) => handleUpdate(showEditModal.channelId, data)}
        />
      )}

      {deleteTarget && (
        <div className="modal-overlay" onMouseDown={event => {
          if (event.target === event.currentTarget && !deleteSubmitting) setDeleteTarget(null);
        }}>
          <div className="modal modal-sm" role="alertdialog" aria-modal="true" aria-labelledby="delete-channel-title"
            onClick={event => event.stopPropagation()}>
            <h3 id="delete-channel-title">Delete notification channel</h3>
            <p>Delete <strong>{deleteTarget.name}</strong>? This cannot be undone.</p>
            {deleteError && <div className="error-message" role="alert">{deleteError}</div>}
            <div className="form-actions">
              <button type="button" className="btn-secondary" disabled={deleteSubmitting}
                onClick={() => { setDeleteTarget(null); setDeleteError(null); }}>Cancel</button>
              <button type="button" className="btn-danger" disabled={deleteSubmitting} onClick={() => void confirmDelete()}>
                {deleteSubmitting ? 'Deleting...' : 'Delete channel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {messageDialog && (
        <div className="modal-overlay" onMouseDown={event => {
          if (event.target === event.currentTarget) setMessageDialog(null);
        }}>
          <div className="modal modal-sm" role="dialog" aria-modal="true" aria-labelledby="channel-message-title"
            onClick={event => event.stopPropagation()}>
            <h3 id="channel-message-title">{messageDialog.title}</h3>
            <p role="status">{messageDialog.message}</p>
            <div className="form-actions">
              <button type="button" className="btn-primary" onClick={() => setMessageDialog(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
      </>}
    </div>
  );
};

// ── Modal Component ──────────────────────────────────────────────────────────

interface ChannelConfig {
  [key: string]: unknown;
  webhookUrl?: string;
  recipients?: string[];
  headers?: Record<string, string>;
  emailProvider?: string;
}

const ChannelModal: React.FC<{
  channel?: NotificationChannel;
  onClose: () => void;
  onSave: (data: { name: string; channelType: string; configJson: string; emailSenderIdentityId?: string | null }) => Promise<void>;
}> = ({ channel, onClose, onSave }) => {
  const isEdit = !!channel;

  const parseConfig = (json?: string): ChannelConfig => {
    try { return json ? JSON.parse(json) : {}; } catch { return {}; }
  };

  const initialConfig = useMemo(() => parseConfig(channel?.configJson), [channel?.configJson]);
  const initialHeadersText = useMemo(() => {
    const headers = initialConfig.headers;
    if (!headers || Object.keys(headers).length === 0) return '';
    return Object.entries(headers).map(([key, value]) => `${key}: ${value}`).join('\n');
  }, [initialConfig]);
  const initialEmailProvider = initialConfig.emailProvider?.toLowerCase() === 'microsoftgraph' ? 'MicrosoftGraph' : 'Legacy';
  const initialValues = useMemo(() => ({
    name: channel?.name ?? '',
    channelType: channel?.channelType ?? 'Teams',
    webhookUrl: initialConfig.webhookUrl ?? '',
    recipients: initialConfig.recipients?.join(', ') ?? '',
    headersText: initialHeadersText,
    emailProvider: initialEmailProvider,
    senderIdentityId: channel?.emailSenderIdentityId || '',
  }), [channel, initialConfig, initialHeadersText, initialEmailProvider]);
  const [name, setName] = useState(initialValues.name);
  const [channelType, setChannelType] = useState<string>(initialValues.channelType);
  const [webhookUrl, setWebhookUrl] = useState(initialValues.webhookUrl);
  const [recipients, setRecipients] = useState(initialValues.recipients);
  const [headersText, setHeadersText] = useState(initialValues.headersText);
  const [emailProvider, setEmailProvider] = useState(initialValues.emailProvider);
  const [senderIdentityId, setSenderIdentityId] = useState(initialValues.senderIdentityId);
  const [eligibleSenders, setEligibleSenders] = useState<ExchangeSenderRegistration[]>([]);
  const [loadingSenders, setLoadingSenders] = useState(false);
  const [senderError, setSenderError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const senderGroups = new Map<string, ExchangeSenderRegistration[]>();
  for (const sender of eligibleSenders) {
    const group = senderGroups.get(sender.mailboxRegistrationId) || [];
    group.push(sender);
    senderGroups.set(sender.mailboxRegistrationId, group);
  }

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changed = name !== initialValues.name || channelType !== initialValues.channelType ||
    webhookUrl !== initialValues.webhookUrl || recipients !== initialValues.recipients ||
    headersText !== initialValues.headersText || emailProvider !== initialValues.emailProvider ||
    senderIdentityId !== initialValues.senderIdentityId;
  const requestClose = () => {
    if (submitting) return;
    if (changed) setConfirmDiscard(true);
    else onClose();
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      if (submitting) return;
      if (confirmDiscard) setConfirmDiscard(false);
      else requestClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [changed, confirmDiscard, submitting]);

  const needsWebhookUrl = ['Teams', 'TeamsWorkflow', 'Webhook'].includes(channelType);
  const needsRecipients = ['Email', 'SMS'].includes(channelType);
  const needsHeaders = channelType === 'Webhook';

  useEffect(() => {
    if (channelType !== 'Email' || emailProvider !== 'MicrosoftGraph') return;
    let current = true;
    setLoadingSenders(true);
    setSenderError(null);
    tenantApi.getEligibleExchangeSenders()
      .then((senders) => { if (current) setEligibleSenders(senders); })
      .catch((loadError) => {
        if (current) {
          setEligibleSenders([]);
          setSenderError(loadError instanceof Error ? loadError.message : 'Eligible senders could not be loaded');
        }
      })
      .finally(() => { if (current) setLoadingSenders(false); });
    return () => { current = false; };
  }, [channelType, emailProvider]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    if (!name.trim()) {
      setError('Name is required.');
      setSubmitting(false);
      return;
    }

    if (needsWebhookUrl && !webhookUrl.trim()) {
      setError('Webhook URL is required for this channel type.');
      setSubmitting(false);
      return;
    }

    if (needsRecipients && !recipients.trim()) {
      setError('At least one recipient is required.');
      setSubmitting(false);
      return;
    }

    if (channelType === 'Email' && emailProvider === 'MicrosoftGraph' &&
      !eligibleSenders.some(sender => sender.senderIdentityId === senderIdentityId)) {
      setError('Select a currently eligible registered sender before saving Graph email.');
      setSubmitting(false);
      return;
    }

    const config: ChannelConfig = parseConfig(channel?.configJson);
    if (needsWebhookUrl) config.webhookUrl = webhookUrl.trim();
    else delete config.webhookUrl;
    if (needsRecipients) config.recipients = recipients.split(',').map(s => s.trim()).filter(Boolean);
    else delete config.recipients;
    if (needsHeaders && headersText.trim()) {
      config.headers = {};
      for (const line of headersText.split('\n')) {
        const idx = line.indexOf(':');
        if (idx > 0) {
          config.headers[line.substring(0, idx).trim()] = line.substring(idx + 1).trim();
        }
      }
    } else delete config.headers;
    if (channelType === 'Email' && emailProvider === 'MicrosoftGraph') config.emailProvider = 'MicrosoftGraph';
    else if (config.emailProvider?.toLowerCase() === 'microsoftgraph') delete config.emailProvider;

    try {
      await onSave({
        name: name.trim(),
        channelType,
        configJson: JSON.stringify(config),
        emailSenderIdentityId: channelType === 'Email' && emailProvider === 'MicrosoftGraph' ? senderIdentityId : null,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to save channel');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={(event) => {
      if (event.target === event.currentTarget) requestClose();
    }}>
      <div className="modal modal-lg" role="dialog" aria-modal="true" aria-labelledby="notification-channel-modal-title"
        onClick={(e) => e.stopPropagation()}>
        <h3 id="notification-channel-modal-title">{isEdit ? 'Edit Notification Channel' : 'Create Notification Channel'}</h3>
        {changed && <p className="help-text" role="status">Unsaved changes</p>}

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Channel Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Ops Team - Teams"
              required
            />
          </div>

          <div className="form-group">
            <label>Channel Type *</label>
            <select value={channelType} onChange={(e) => setChannelType(e.target.value)}>
              {CHANNEL_TYPES.map((ct) => (
                <option key={ct.value} value={ct.value}>{ct.label}</option>
              ))}
            </select>
            <p className="help-text">
              {CHANNEL_TYPES.find(t => t.value === channelType)?.description}
            </p>
          </div>

          {needsWebhookUrl && (
            <div className="form-group">
              <label>Webhook URL *</label>
              <input
                type="url"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder={channelType === 'TeamsWorkflow'
                  ? 'https://prod-xx.westus.logic.azure.com:443/workflows/...'
                  : 'https://outlook.office.com/webhook/...'}
                required
              />
              {channelType === 'Teams' && (
                <p className="help-text">Paste the Incoming Webhook connector URL from your Teams channel.</p>
              )}
              {channelType === 'TeamsWorkflow' && (
                <p className="help-text">Paste the Power Automate / Workflow HTTP trigger URL.</p>
              )}
            </div>
          )}

          {needsRecipients && (
            <div className="form-group">
              <label>{channelType === 'Email' ? 'Default/Fallback Email Addresses (comma separated)' : 'Phone Numbers (comma-separated)'} *</label>
              <input
                type="text"
                value={recipients}
                onChange={(e) => setRecipients(e.target.value)}
                placeholder={channelType === 'Email' ? 'ops@example.com, team@example.com' : '+61400000000, +61400000001'}
                required
              />
            </div>
          )}

          {channelType === 'Email' && (
            <>
              <div className="form-group">
                <label>Email Provider</label>
                <select aria-label="Email provider" value={emailProvider} onChange={(event) => setEmailProvider(event.target.value)}>
                  <option value="Legacy">Existing provider behavior</option>
                  <option value="MicrosoftGraph">Microsoft Graph shared mailbox</option>
                </select>
              </div>
              {emailProvider === 'MicrosoftGraph' && (
                <div className="form-group">
                  <label>Registered send address</label>
                  <select aria-label="Registered send address" value={senderIdentityId} onChange={(event) => setSenderIdentityId(event.target.value)} disabled={loadingSenders} required>
                    <option value="">Select an eligible sender</option>
                    {channel?.emailSenderIdentityId && !eligibleSenders.some(sender => sender.senderIdentityId === channel.emailSenderIdentityId) && (
                      <option value={channel.emailSenderIdentityId} disabled>Saved sender unavailable</option>
                    )}
                    {Array.from(senderGroups, ([mailboxId, senders]) => (
                      <optgroup key={mailboxId} label={senders[0].mailboxName}>
                        {senders.map(sender => (
                          <option key={sender.senderIdentityId} value={sender.senderIdentityId}>
                            {sender.label} &lt;{sender.fromAddress}&gt;
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {loadingSenders && <p className="help-text">Loading eligible senders...</p>}
                  {senderError && <div className="error-message" role="alert">{senderError}</div>}
                  {!loadingSenders && !senderError && !eligibleSenders.length && (
                    <p className="help-text">No eligible registered senders.</p>
                  )}
                </div>
              )}
            </>
          )}

          {needsHeaders && (
            <div className="form-group">
              <label>Custom Headers (one per line, Key: Value)</label>
              <textarea
                value={headersText}
                onChange={(e) => setHeadersText(e.target.value)}
                rows={3}
                placeholder="Authorization: Bearer xxx&#10;X-Custom-Header: value"
              />
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn-secondary" disabled={submitting} onClick={requestClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Channel'}
            </button>
          </div>
        </form>
        {confirmDiscard && <div className="modal-overlay" onMouseDown={event => {
          if (event.target === event.currentTarget) setConfirmDiscard(false);
        }}>
          <div className="modal modal-sm" role="alertdialog" aria-modal="true" aria-labelledby="channel-discard-title"
            onClick={event => event.stopPropagation()}>
            <h3 id="channel-discard-title">Discard unsaved changes?</h3>
            <p>Your notification channel changes have not been saved.</p>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setConfirmDiscard(false)}>Keep editing</button>
              <button type="button" className="btn-danger" onClick={onClose}>Discard changes</button>
            </div>
          </div>
        </div>}
      </div>
    </div>
  );
};
